import { Component, OnInit, Input, OnChanges, SimpleChanges , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';

import { Tooltip } from 'primeng/tooltip';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { WorkNoteService } from '../../services/work-note.service';
import { WorkNote, CreateWorkNoteDto, WorkNoteNotificationResult } from '../../models/work-note.model';
import { AuthService, UserInfo } from '../../services/auth.service';
import { catchError, of } from 'rxjs';

@Component({
  selector: 'app-work-notes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Button,
    Tooltip,
    ConfirmDialog
  ],
  templateUrl: './work-notes.component.html',
  styleUrl: './work-notes.component.scss',
  providers: [ConfirmationService]
})
export class WorkNotesComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() projectId: string = '';
  @Input() readonly = false; // When true (e.g. Sales), hide add/delete/reply (view-only)

  notes: WorkNote[] = [];
  newNote: string = '';
  loading: boolean = false;
  user: UserInfo | null = null;
  sendEmailNotification: boolean = true;
  replyingToNote: WorkNote | null = null;
  replyText: string = '';

  constructor(
    private workNoteService: WorkNoteService,
    private authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit() {
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.user = user;
    });

    if (this.projectId) {
      this.loadNotes();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && !changes['projectId'].firstChange && this.projectId) {
      this.loadNotes();
    }
  }

  loadNotes() {
    if (!this.projectId) return;

    this.loading = true;
    this.workNoteService.getByProjectId(this.projectId).pipe(
      catchError(error => {
        console.error('Error loading work notes:', error);
        // Don't show error if backend is not ready yet
        if (error.status !== 404) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load work notes'
          });
        }
        return of([]);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (notes) => {
        // Notes are already structured with replies from backend
        this.notes = notes.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        this.loading = false;
      }
    });
  }

  getAllNotes(notes: WorkNote[]): WorkNote[] {
    let allNotes: WorkNote[] = [];
    notes.forEach(note => {
      allNotes.push(note);
      if (note.replies && note.replies.length > 0) {
        allNotes = allNotes.concat(this.getAllNotes(note.replies));
      }
    });
    return allNotes;
  }

  addNote() {
    if (!this.newNote.trim() || !this.user || !this.projectId) {
      return;
    }

    const createDto: CreateWorkNoteDto = {
      projectId: this.projectId,
      note: this.newNote.trim(),
      sendEmailNotification: this.sendEmailNotification
    };

    this.loading = true;
    this.workNoteService.addNote(createDto).pipe(
      catchError(error => {
        console.error('Error adding work note:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add work note. Please ensure the backend API is available.'
        });
        this.loading = false;
        return of(null);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (note) => {
        if (note) {
          this.notes.unshift(note);
          this.newNote = '';
          this.showNotificationOutcome(note.notificationResult, 'Work note added successfully');
        }
        this.loading = false;
      }
    });
  }

  /**
   * Reports what actually happened to the email, using the outcome the API sends back rather
   * than the state of the checkbox. A note that saved but notified nobody is a warning, not a
   * success — previously it was reported as "email notification sent" either way.
   *
   * An older API build omits notificationResult, so fall back to the plain saved-ok message.
   */
  private showNotificationOutcome(result: WorkNoteNotificationResult | undefined, fallback: string) {
    if (!result) {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: fallback });
      return;
    }

    const isProblem = result.status === 'NoRecipients' || result.status === 'Failed';
    this.messageService.add({
      severity: isProblem ? 'warn' : 'success',
      summary: isProblem ? 'Saved, but not emailed' : 'Success',
      detail: result.message || fallback,
      // Give the reader time to actually absorb a problem message.
      life: isProblem ? 10000 : undefined
    });
  }

  startReply(note: WorkNote) {
    this.replyingToNote = note;
    this.replyText = '';
  }

  cancelReply() {
    this.replyingToNote = null;
    this.replyText = '';
  }

  addReply() {
    if (!this.replyText.trim() || !this.user || !this.projectId || !this.replyingToNote) {
      return;
    }

    const createDto: CreateWorkNoteDto = {
      projectId: this.projectId,
      note: this.replyText.trim(),
      parentNoteId: this.replyingToNote.id,
      sendEmailNotification: this.sendEmailNotification
    };

    this.loading = true;
    this.workNoteService.addNote(createDto).pipe(
      catchError(error => {
        console.error('Error adding reply:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add reply'
        });
        this.loading = false;
        return of(null);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (reply) => {
        if (reply) {
          // Add reply to the parent note
          if (!this.replyingToNote!.replies) {
            this.replyingToNote!.replies = [];
          }
          this.replyingToNote!.replies.push(reply);
          this.cancelReply();
          this.showNotificationOutcome(reply.notificationResult, 'Reply added successfully');
        }
        this.loading = false;
      }
    });
  }

  deleteNote(note: WorkNote) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this note? This action cannot be undone.',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.workNoteService.deleteNote(note.id).pipe(
          catchError(error => {
            console.error('Error deleting work note:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete work note'
            });
            return of(null);
          })
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            // Remove from notes or replies
            this.removeNoteFromTree(this.notes, note.id);
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Work note deleted successfully'
            });
          }
        });
      }
    });
  }

  private removeNoteFromTree(notes: WorkNote[], id: string): boolean {
    for (let i = 0; i < notes.length; i++) {
      if (notes[i].id === id) {
        notes.splice(i, 1);
        return true;
      }
      if (notes[i].replies && this.removeNoteFromTree(notes[i].replies!, id)) {
        return true;
      }
    }
    return false;
  }

  formatDate(dateString: string): string {
    if (!dateString) {
      return 'Unknown';
    }

    let date: Date;
    let trimmedDate = dateString.trim();
    
    // Check if date already has timezone info
    const hasTimezone = trimmedDate.endsWith('Z') || 
                         trimmedDate.match(/[+-]\d{2}:?\d{2}$/) !== null ||
                         trimmedDate.match(/[+-]\d{4}$/) !== null;
    
    if (hasTimezone) {
      // Already has timezone info, parse normally
      date = new Date(trimmedDate);
    } else {
      
      if (trimmedDate.includes(' ')) {
        trimmedDate = trimmedDate.replace(/\s+/, 'T'); // Replace first whitespace with T
      }
      // Always append 'Z' to treat as UTC since backend stores in UTC
      date = new Date(trimmedDate + 'Z');
    }

    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    const now = new Date();
    // Get current time in UTC explicitly
    const nowUtc = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds()
    );
    const dateUtc = date.getTime(); // UTC milliseconds
    const diffInSeconds = Math.floor((nowUtc - dateUtc) / 1000);
    
    // Handle negative differences (future dates) - should not happen but just in case
    if (diffInSeconds < 0) {
      return 'just now';
    }

    // Less than a minute
    if (diffInSeconds < 60) {
      return diffInSeconds < 1 ? 'just now' : `${diffInSeconds}s`;
    }

    // Less than an hour
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    }

    // Less than a day
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h`;
    }

    // Less than a week
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays}d`;
    }

    // Less than a month
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return `${diffInWeeks}w`;
    }

    // Less than a year
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths}mo`;
    }

    // More than a year - show actual date
    const diffInYears = Math.floor(diffInDays / 365);
    if (diffInYears >= 1) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  isOwnNote(note: WorkNote): boolean {
    return this.user?.email?.toLowerCase() === note.authorEmail?.toLowerCase();
  }

  canDeleteNote(note: WorkNote): boolean {
    // Only allow deletion if user is admin, finance, or the note author
    return this.user ? 
      (this.user.isAdmin || this.user.isFinance || this.isOwnNote(note)) : 
      false;
  }
}

